import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Schedule a single setTimeout callback safely from a React component.
 *
 * Tracks the pending handle in a ref, clears any prior pending handle before
 * scheduling a new one, and clears it on unmount. Same shape as the
 * focus-follow / refocus timers added in F1-009 / F1-010 / F20-002 / F25-002
 * across DayGrid / WeekGrid / MonthGrid / TimeEntryPage / MoveTasks / MergeTasks.
 *
 * Returns `{ schedule, cancel }`:
 *   - `schedule(fn, ms)` cancels any pending callback then schedules `fn` after
 *     `ms` milliseconds. When `fn` runs it auto-clears the internal handle.
 *   - `cancel()` clears any pending callback without scheduling a new one.
 */
export function useTrackedTimeout(): {
  schedule: (fn: () => void, ms: number) => void;
  cancel: () => void;
} {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const schedule = useCallback(
    (fn: () => void, ms: number) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        fn();
      }, ms);
    },
    [],
  );

  useEffect(() => cancel, [cancel]);

  // Stable object identity so consumers can list the returned value in
  // hook deps arrays without invalidating their useCallback / useEffect
  // each render.
  return useMemo(() => ({ schedule, cancel }), [schedule, cancel]);
}
